import { useQuery } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { analyticsApi } from '@/lib/api'
import { cn } from '@/lib/utils'
import { cardTitleStyle } from '@/lib/styleKit'
import { EmptyChart } from './EmptyChart'
import { ShieldCheck, Check, X as XIcon } from '@phosphor-icons/react'
import {
  LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts'
import { useReducedMotion } from 'framer-motion'
import { DotLoader } from '@/components/ui/DotLoader'
import { seriesColor, seriesDash, tooltipContentStyle, useChartTheme } from './chartTheme'
import type { DeliverabilityData, HealthScore, Period } from '@/types/analytics'

interface TabProps {
  period: Period | null
  startDate: string | null
  endDate: string | null
  userId: number | null
  /** One mailbox address, or undefined for the combined view. */
  senderEmail?: string
}

// Health-status chrome — soft fill + matching border + text (dual-mode).
const STATUS_CHIP: Record<string, string> = {
  healthy:  'bg-emerald-500/10 text-emerald-700 border-emerald-500/25 dark:bg-emerald-500/[0.12] dark:text-emerald-400 dark:border-emerald-500/25',
  warning:  'bg-amber-500/10 text-amber-700 border-amber-500/25 dark:bg-amber-500/[0.12] dark:text-amber-400 dark:border-amber-500/25',
  critical: 'bg-red-500/10 text-red-700 border-red-500/25 dark:bg-red-500/[0.12] dark:text-red-400 dark:border-red-500/25',
}


// §60: never render a raw status enum. These three are English words so the old
// `{score.status}` read acceptably, but a value the backend adds later would not.
const STATUS_LABELS: Record<string, string> = {
  healthy:  'Healthy',
  warning:  'Warning',
  critical: 'Critical',
}

function statusLabel(status?: string | null): string {
  const raw = (status || '').trim()
  if (!raw) return 'Unknown'
  return STATUS_LABELS[raw.toLowerCase()] || raw.replace(/[_-]+/g, ' ')
}

const HEALTH_LABELS: Record<string, string> = {
  delivery_rate:        'Delivery rate',
  bounce_rate:          'Bounce rate',
  unsubscribe_rate:     'Unsubscribe rate',
  spam_complaint_rate:  'Spam complaint rate',
}

// This file's former `STATUS_BAR_COLORS` — the one scale in the app that was
// actually CVD-measured — is now `SERIES_COLORS` in `chartTheme` (§61), values
// unchanged, so the same six hues mean the same six things on every tab.
const BREAKDOWN_LABELS: Record<string, string> = {
  delivered:    'Delivered',
  opened:       'Opened',
  clicked:      'Clicked',
  replied:      'Replied',
  bounced:      'Bounced',
  unsubscribed: 'Unsubscribed',
}

// Lifecycle order, not the API's key order — and it is also the nesting order:
// opens, clicks and replies are subsets of `delivered`, which is why these six
// rates must never be stacked (see the geometry note below).
const BREAKDOWN_ORDER = ['delivered', 'opened', 'clicked', 'replied', 'bounced', 'unsubscribed'] as const

// The three measures that count a subset of `delivered` rather than a slice of
// its own. Called out in the UI so a reader is not left to work out why the
// column does not add up to 100%.
const NESTED_IN_DELIVERED = new Set<string>(['opened', 'clicked', 'replied'])

const TREND_SERIES = [
  { key: 'bounce_rate',      label: 'Bounce %' },
  { key: 'unsubscribe_rate', label: 'Unsub %' },
] as const

const cardCls = cn(
  'rounded-xl border transition-colors duration-150',
  'bg-card border-border/50',
  'dark:bg-white/[0.02] dark:border-white/[0.08]',
)

function HealthScoreCard({ name, score }: { name: string; score: HealthScore }) {
  const statusKey = score.status || 'healthy'
  const chip      = STATUS_CHIP[statusKey] || STATUS_CHIP.healthy

  return (
    <div
      className={cn(
        'rounded-xl border p-5 transition-colors duration-150',
        'bg-card border-border/50 dark:bg-white/[0.02] dark:border-white/[0.08]',
      )}
    >
      <div
        className="text-muted-foreground dark:text-[#8a8f98] uppercase mb-1.5"
        style={{ fontSize: '10px', fontWeight: 590, letterSpacing: '0.08em' }}
      >
        {HEALTH_LABELS[name] || name}
      </div>
      <div
        className="tabular-nums text-foreground dark:text-[#f7f8f8]"
        style={{ fontSize: '28px', fontWeight: 590, letterSpacing: '-0.5px', lineHeight: 1.05 }}
      >
        {score.value.toFixed(1)}%
      </div>
      <div className="flex items-center gap-1 mt-2">
        <span
          className={cn('inline-flex items-center px-2 py-0.5 rounded-full border uppercase', chip)}
          style={{ fontSize: '10px', fontWeight: 590, letterSpacing: '0.04em' }}
        >
          {statusLabel(score.status)}
        </span>
      </div>
      {(score.benchmark || score.threshold) && (
        <div
          className="text-muted-foreground dark:text-[#8a8f98] mt-2.5"
          style={{ fontSize: '11px', letterSpacing: '-0.05px' }}
        >
          {score.benchmark && <span>Benchmark: {score.benchmark}</span>}
          {score.benchmark && score.threshold && <span> · </span>}
          {score.threshold && <span>Threshold: {score.threshold}</span>}
        </div>
      )}
    </div>
  )
}

function AuthBadge({ label, valid }: { label: string; valid: boolean }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 px-2 py-0.5 rounded-full border',
        valid
          ? 'bg-emerald-500/10 text-emerald-700 border-emerald-500/25 dark:bg-emerald-500/[0.12] dark:text-emerald-400 dark:border-emerald-500/25'
          : 'bg-red-500/10 text-red-700 border-red-500/25 dark:bg-red-500/[0.12] dark:text-red-400 dark:border-red-500/25',
      )}
      style={{ fontSize: '10px', fontWeight: 590, letterSpacing: '0.04em' }}
    >
      {valid
        ? <Check className="h-3 w-3" weight="bold" />
        : <XIcon className="h-3 w-3" weight="bold" />}
      {label}
    </span>
  )
}

export default function DeliverabilityTab({ period, startDate, endDate, userId, senderEmail }: TabProps) {
  const chartTheme = useChartTheme()
  // Recharts animates via react-smooth (JS-driven), so it ignores both the global
  // CSS reduced-motion guard and framer-motion's <MotionConfig>. Gate it explicitly.
  const prefersReduced = useReducedMotion()
  const { data, isLoading } = useQuery<DeliverabilityData>({
    queryKey: ['analytics', 'deliverability', period, startDate, endDate, userId, senderEmail],
    queryFn: () =>
      analyticsApi
        .getDeliverability({ period: period || undefined, start: startDate || undefined, end: endDate || undefined, user_id: userId ?? undefined, sender_email: senderEmail })
        .then((r) => r.data),
    staleTime: 5 * 1000,
    refetchOnWindowFocus: true,
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24 text-[#5e6ad2] dark:text-[#a5b4fc]">
        <DotLoader size="lg" label="Loading deliverability data" />
      </div>
    )
  }

  if (!data) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center max-w-md mx-auto">
        <div className="h-12 w-12 rounded-md bg-muted/70 dark:bg-white/[0.04] dark:border dark:border-white/[0.05] flex items-center justify-center mb-4">
          <ShieldCheck className="h-5 w-5 text-muted-foreground dark:text-[#8a8f98]" weight="regular" />
        </div>
        <h3
          className="text-foreground dark:text-[#f7f8f8] mb-2"
          style={{ fontSize: '20px', fontWeight: 590, letterSpacing: '-0.24px', lineHeight: 1.33 }}
        >
          No deliverability data
        </h3>
        <p
          className="text-muted-foreground dark:text-[#8a8f98] leading-relaxed"
          style={{ fontSize: '13px', letterSpacing: '-0.13px' }}
        >
          No deliverability data available for this period.
        </p>
      </div>
    )
  }

  // The six values are each an *independent* rate of total sent, not six slices
  // of one whole: `opened`/`clicked`/`replied` count subsets of `delivered`, so
  // they sum to ~156%. Stacking them in one 100% bar and dividing each by that
  // sum rendered a 98% delivery rate as 63% of the bar while printing the true
  // "98.0%" beside it. Each rate now owns a 0-100% track, so the geometry *is*
  // the number.
  const breakdown = data.email_status_breakdown as Record<string, number | undefined>
  const breakdownRates = BREAKDOWN_ORDER.filter((key) => typeof breakdown[key] === 'number').map(
    (key) => ({
      key,
      label: BREAKDOWN_LABELS[key],
      value: breakdown[key] as number,
      nested: NESTED_IN_DELIVERED.has(key),
    }),
  )
  // Every rate is kept visible even at 0.0% — an all-zero row is a fact, and
  // hiding it would make the reader guess whether the metric exists
  // (`CampaignsTab`'s fixed-legend pattern).
  const hasBreakdown = breakdownRates.some((r) => r.value > 0)

  return (
    <div className="space-y-5">
      {/* Row 1: Health Score Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {(Object.keys(data.health_scores) as Array<keyof typeof data.health_scores>).map((key) => (
          <HealthScoreCard key={key} name={key} score={data.health_scores[key]} />
        ))}
      </div>

      {/* Row 2: Trends + Sender Accounts */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">
        <Card className={cn(cardCls, 'lg:col-span-3')}>
          <CardHeader>
            <CardTitle className="text-foreground dark:text-[#f7f8f8]" style={cardTitleStyle}>
              Bounce &amp; unsubscribe trends
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.trends.length > 0 ? (
              // `role="group"`, not a bare labelled div: `aria-label` on a
              // generic div is dropped by AT, and `role="img"` would hide the
              // series `accessibilityLayer` just made explorable.
              <div role="group" aria-label="Bounce and unsubscribe rate per week, as a percentage of emails sent">
              <ResponsiveContainer width="100%" height={280}>
                <LineChart accessibilityLayer data={data.trends} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.grid} vertical={false} />
                  <XAxis dataKey="week" tick={{ fontSize: 11, fill: chartTheme.tickFill }} stroke={chartTheme.axis} strokeWidth={1} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: chartTheme.tickFill }} stroke={chartTheme.axis} strokeWidth={1} tickLine={false} axisLine={false} unit="%" />
                  <Tooltip
                    cursor={{ stroke: chartTheme.axis, strokeDasharray: '3 3' }}
                    contentStyle={tooltipContentStyle(chartTheme)}
                    labelStyle={{ color: chartTheme.tooltipText, fontWeight: 590, marginBottom: 4 }}
                  />
                  <Legend wrapperStyle={{ fontSize: '12px', paddingTop: 8 }} iconType="circle" iconSize={8} />
                  {TREND_SERIES.map(({ key, label }) => (
                    <Line
                      key={key}
                      type="monotone"
                      dataKey={key}
                      name={label}
                      stroke={seriesColor(key)}
                      strokeDasharray={seriesDash(key)}
                      strokeWidth={2}
                      dot={false}
                      isAnimationActive={!prefersReduced}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
              </div>
            ) : (
              <EmptyChart message="No trend data yet." icon={<ShieldCheck className="h-5 w-5" weight="regular" />} />
            )}
          </CardContent>
        </Card>

        <Card className={cn(cardCls, 'lg:col-span-2')}>
          <CardHeader>
            <CardTitle className="text-foreground dark:text-[#f7f8f8]" style={cardTitleStyle}>
              Sender accounts
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.senders.length > 0 ? (
              <div className="space-y-2.5">
                {data.senders.map((sender) => {
                  const statusKey = sender.status || 'healthy'
                  const chip = STATUS_CHIP[statusKey] || STATUS_CHIP.healthy
                  return (
                    <div
                      key={sender.id}
                      className={cn(
                        'rounded-lg border p-3.5 space-y-2.5 transition-colors duration-150',
                        'bg-card border-border/60 hover:border-border/80',
                        'dark:bg-white/[0.02] dark:border-white/[0.08] dark:hover:border-white/[0.14]',
                      )}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span
                          className="text-foreground dark:text-[#f7f8f8] truncate"
                          style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                        >
                          {sender.email}
                        </span>
                        <span
                          className={cn('inline-flex items-center px-2 py-0.5 rounded-full border uppercase flex-shrink-0', chip)}
                          style={{ fontSize: '10px', fontWeight: 590, letterSpacing: '0.04em' }}
                        >
                          {statusLabel(sender.status)}
                        </span>
                      </div>
                      <div
                        className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-muted-foreground dark:text-[#8a8f98]"
                        style={{ fontSize: '11px', letterSpacing: '-0.1px' }}
                      >
                        <span>
                          Reputation:{' '}
                          <span className="text-foreground dark:text-[#f7f8f8] tabular-nums" style={{ fontWeight: 590 }}>
                            {sender.reputation_score}/100
                          </span>
                        </span>
                        <span>
                          Bounce:{' '}
                          <span className="text-foreground dark:text-[#f7f8f8] tabular-nums" style={{ fontWeight: 590 }}>
                            {sender.bounce_rate.toFixed(1)}%
                          </span>
                        </span>
                        <span>
                          Sent:{' '}
                          <span className="text-foreground dark:text-[#f7f8f8] tabular-nums" style={{ fontWeight: 590 }}>
                            {sender.sent_today}/{sender.daily_limit}
                          </span>
                        </span>
                      </div>
                      <div className="flex gap-1.5 pt-0.5">
                        <AuthBadge label="SPF"   valid={sender.spf_valid} />
                        <AuthBadge label="DKIM"  valid={sender.dkim_valid} />
                        <AuthBadge label="DMARC" valid={sender.dmarc_valid} />
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <EmptyChart message="No sender accounts configured." icon={<ShieldCheck className="h-5 w-5" weight="regular" />} />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Row 3: Email Status Breakdown */}
      <Card className={cardCls}>
        <CardHeader>
          <CardTitle className="text-foreground dark:text-[#f7f8f8]" style={cardTitleStyle}>
            Email status breakdown
          </CardTitle>
        </CardHeader>
        <CardContent>
          {hasBreakdown ? (
            <div className="space-y-3.5">
              <p
                className="text-muted-foreground dark:text-[#8a8f98] leading-relaxed"
                style={{ fontSize: '11px', letterSpacing: '-0.05px' }}
              >
                Each rate is a share of all emails sent. Opens, clicks and replies count
                delivered emails, so they overlap with Delivered rather than adding to it —
                these do not sum to 100%.
              </p>
              {/* Every label starts on the same left edge and every track on the
                  same x. Two earlier attempts got this wrong: indenting the row
                  (`nested && 'pl-3'`) shortened nested tracks by 12px, so a
                  36.9% bar was measured against a different baseline than the
                  82.7% bar above it; moving that indent inside the label column
                  fixed the tracks but left the labels ragged for no gain. The
                  nesting is a *semantic* relationship — opens/clicks/replies are
                  subsets of delivered — and the paragraph above states it in
                  words, so it is carried by label weight here, not geometry. */}
              {breakdownRates.map(({ key, label, value, nested }) => (
                <div key={key} className="flex items-center gap-3">
                  <span
                    className={cn(
                      'w-28 flex-shrink-0 truncate',
                      nested
                        ? 'text-muted-foreground dark:text-[#8a8f98]'
                        : 'text-foreground dark:text-[#f7f8f8]',
                    )}
                    style={{ fontSize: '12px', fontWeight: nested ? 460 : 510, letterSpacing: '-0.1px' }}
                  >
                    {label}
                  </span>
                  <div
                    className="relative flex-1 min-w-0 h-2.5 rounded-full overflow-hidden bg-muted/50 dark:bg-white/[0.06]"
                    aria-hidden="true"
                  >
                    <div
                      className="h-full rounded-full transition-all duration-300"
                      style={{
                        width: `${Math.min(Math.max(value, 0), 100)}%`,
                        backgroundColor: seriesColor(key),
                      }}
                    />
                  </div>
                  <span
                    className="text-foreground dark:text-[#f7f8f8] w-14 text-right tabular-nums flex-shrink-0"
                    style={{ fontSize: '12px', fontWeight: 590, letterSpacing: '-0.1px' }}
                  >
                    {value.toFixed(1)}%
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <EmptyChart message="No email status data yet." icon={<ShieldCheck className="h-5 w-5" weight="regular" />} />
          )}
        </CardContent>
      </Card>
    </div>
  )
}
